import type { ButtonHTMLAttributes, CSSProperties, FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'

export type ButtonVariant = 'primary' | 'outline' | 'dark' | 'outline-dark' | 'light'
export type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonStyleOptions {
    variant?: ButtonVariant
    size?: ButtonSize
    className?: string
}

const BASE = 'group/btn relative isolate inline-flex shrink-0 items-center justify-center gap-2 overflow-hidden whitespace-nowrap font-medium tracking-[-0.01em] transition-[background-color,border-color,color,box-shadow,transform] duration-300 ease-calm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50'

const VARIANTS: Record<ButtonVariant, string> = {
    'primary': 'rounded-full bg-ultramarine text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.22),0_10px_24px_-12px_rgba(47,55,255,0.75)] hover:bg-ultramarine-deep focus-visible:ring-ultramarine focus-visible:ring-offset-cream',
    'outline': 'rounded-full border border-ink/15 bg-paper/60 text-ink backdrop-blur hover:border-ink/30 hover:bg-paper focus-visible:ring-ultramarine focus-visible:ring-offset-cream',
    'light': 'rounded-full bg-white text-ink shadow-[0_10px_24px_-14px_rgba(0,0,0,0.6)] hover:bg-cream focus-visible:ring-white focus-visible:ring-offset-charcoal',
    'dark': 'rounded-xl border border-white/10 bg-charcoal-3 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.09),0_12px_28px_-14px_rgba(0,0,0,0.9)] hover:border-white/20 hover:bg-[#2d2b26] focus-visible:ring-white/70 focus-visible:ring-offset-charcoal',
    'outline-dark': 'rounded-full border border-white/15 text-white hover:border-white/30 hover:bg-white/5 focus-visible:ring-white/70 focus-visible:ring-offset-charcoal'
}

const SIZES: Record<ButtonSize, string> = {
    sm: 'h-9 px-4 text-[13px]',
    md: 'h-11 px-5 text-sm',
    lg: 'h-12 px-6 text-[15px]'
}

const SHEEN_VARIANTS: Array<ButtonVariant> = ['primary', 'outline', 'light']

const SHEEN_STYLE: CSSProperties = {
    transform: 'translate3d(-160%, 0, 0) skewX(-18deg)'
}

export const buttonClasses = ({ variant = 'primary', size = 'md', className }: ButtonStyleOptions = {}): string => {
    return cn(BASE, VARIANTS[variant], SIZES[size], className)
}

const Sheen: FC<{ variant: ButtonVariant }> = ({ variant }): ReactNode => {
    if (!SHEEN_VARIANTS.includes(variant)) return null

    return (
        <span
            aria-hidden='true'
            style={SHEEN_STYLE}
            className={cn(
                'pointer-events-none absolute inset-y-0 left-0 -z-10 w-1/3 bg-gradient-to-r from-transparent to-transparent group-hover/btn:animate-sheen',
                variant === 'outline' ? 'via-white/80' : 'via-white/35'
            )}
        />
    )
}

interface ButtonLinkProps extends ButtonStyleOptions {
    href: string
    children: ReactNode
    target?: string
    rel?: string
    onClick?: () => void
    'aria-label'?: string
}

export const ButtonLink: FC<ButtonLinkProps> = ({
    href,
    children,
    variant = 'primary',
    size = 'md',
    className,
    ...rest
}): ReactNode => {
    const classes = buttonClasses({ variant, size, className })
    const external = /^(https?:|mailto:|tel:)/.test(href)

    if (external) {
        return (
            <a href={href} className={classes} {...rest}>
                <Sheen variant={variant} />
                {children}
            </a>
        )
    }

    return (
        <Link href={href} className={classes} {...rest}>
            <Sheen variant={variant} />
            {children}
        </Link>
    )
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, ButtonStyleOptions {
    children: ReactNode
}

export const Button: FC<ButtonProps> = ({
    children,
    variant = 'primary',
    size = 'md',
    className,
    type = 'button',
    ...rest
}): ReactNode => {
    return (
        <button type={type} className={buttonClasses({ variant, size, className })} {...rest}>
            <Sheen variant={variant} />
            {children}
        </button>
    )
}

interface ArrowLinkProps {
    href: string
    children: ReactNode
    className?: string
    tone?: 'ink' | 'ultramarine' | 'light'
}

/** Text link with an arrow that nudges forward on hover. */
export const ArrowLink: FC<ArrowLinkProps> = ({ href, children, className, tone = 'ink' }): ReactNode => {
    return (
        <Link
            href={href}
            className={cn(
                'group/arrow inline-flex items-center gap-1.5 text-sm font-medium underline-offset-4 transition-colors focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ultramarine',
                tone === 'ink' && 'text-ink hover:text-ultramarine',
                tone === 'ultramarine' && 'text-ultramarine hover:text-ultramarine-deep',
                tone === 'light' && 'text-white/80 hover:text-white',
                className
            )}
        >
            {children}
            <ArrowRight aria-hidden='true' className='size-4 transition-transform duration-300 ease-calm group-hover/arrow:translate-x-1' />
        </Link>
    )
}
